import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { apiAdminpointsGetCollection } from '@r/api/generated/client.ts';
import { unparse } from 'papaparse';
import { downloadCSV } from '@r/utils/utils.ts';
import type {
  ApiAdminpointsGetCollectionParams,
  PointPointCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useUser } from '@r/api/auth.tsx';
import { format as formatDate } from 'date-fns';
import {
  usePointsColumns,
  usePointsFilterFields,
  type PointCollection
} from '@admin/components/points/points-columns.tsx';
import { buildApiDateRangeParams } from '@r/utils/date-range-filter.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type PointFilters = {
  companyName: string;
  companyUniqueCode: string;
  label: string;
  beneficiaryFullname: string;
  amountMin: string;
  amountMax: string;
  status: string[] | undefined;
  valueDate: string;
  valueDateAfter: string;
  valueDateBefore: string;
  createdAt: string;
  createdAtAfter: string;
  createdAtBefore: string;
  importedByFullname: string;
  amountSortValue: 'asc' | 'desc' | undefined;
  createdAtSortValue: 'asc' | 'desc' | undefined;
  valueDateSortValue: 'asc' | 'desc' | undefined;
};

type PointsQueryResult = {
  data: PointCollection[];
  pagination?: { last?: number; totalItems?: number };
};

export const Points = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);
  const user = useUser();
  const { sanitizedWorkspaceName } = useWorkspaceName();

  function mapStatusToTypes(statuses: string[] | undefined): number[] | undefined {
    if (!statuses || statuses.length === 0) return undefined;
    const mapping: Record<string, number> = {
      CORE: 0,
      IMPORTED: 1,
      SPENT: 2,
      EXPIRED: 3,
      CANCELED: 4
    };
    const result = statuses
      .map((s) => mapping[s])
      .filter((v): v is number => typeof v === 'number');
    return result.length ? result : undefined;
  }

  async function pointsListFiltersQuery(
    _filters: PointFilters,
    page: number | null = null,
    limit: number | null = null
  ): Promise<PointsQueryResult> {
    try {
      const {
        companyName,
        companyUniqueCode,
        label,
        beneficiaryFullname,
        amountMin,
        amountMax,
        status,
        valueDate,
        valueDateAfter,
        valueDateBefore,
        createdAt,
        createdAtAfter,
        createdAtBefore,
        importedByFullname,
        amountSortValue,
        createdAtSortValue,
        valueDateSortValue
      } = _filters;

      const typeArray = mapStatusToTypes(status);

      const queriesPayload: ApiAdminpointsGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(companyName ? { 'attributedToCompany.name': companyName } : {}),
        ...(companyUniqueCode ? { 'attributedToCompany.uniqueCode': companyUniqueCode } : {}),
        ...(label ? { label } : {}),
        ...(beneficiaryFullname ? { 'obtainedBy.user.fullname': beneficiaryFullname } : {}),
        ...(amountMin ? { 'amount[gte]': amountMin } : {}),
        ...(amountMax ? { 'amount[lte]': amountMax } : {}),
        ...(typeArray ? { 'type[]': typeArray } : {}),
        ...(importedByFullname ? { 'addedByUserWorkspace.user.fullname': importedByFullname } : {}),
        ...(valueDateAfter ? { 'valueDate[after]': valueDateAfter } : {}),
        ...(valueDateBefore ? { 'valueDate[before]': valueDateBefore } : {}),
        ...(!valueDateAfter && !valueDateBefore
          ? buildApiDateRangeParams('valueDate', valueDate)
          : {}),
        ...(createdAtAfter ? { 'createdAt[after]': createdAtAfter } : {}),
        ...(createdAtBefore ? { 'createdAt[before]': createdAtBefore } : {}),
        ...(!createdAtAfter && !createdAtBefore
          ? buildApiDateRangeParams('createdAt', createdAt)
          : {}),
        ...(amountSortValue ? { 'order[amount]': amountSortValue } : {}),
        ...(createdAtSortValue ? { 'order[createdAt]': createdAtSortValue } : {}),
        ...(valueDateSortValue ? { 'order[valueDate]': valueDateSortValue } : {}),
        ...(valueDateSortValue ? { 'order[id]': valueDateSortValue } : {})
      };

      const response = await apiAdminpointsGetCollection(queriesPayload);

      type ApiPoint = PointPointCollectionReadModel;
      const mappedData: PointCollection[] =
        (response?.data as ApiPoint[] | undefined)?.map((p) => ({
          id: p.id,
          amount: p.amount,
          label: (p as unknown as { label?: string | null })?.label ?? null,
          status: (p.typeLabel as unknown as PointCollection['status']) ?? undefined,
          valueDate: p.valueDate ?? null,
          importedAt: (p as unknown as { createdAt?: string | null })?.createdAt ?? null,
          importedBy: {
            fullname: p.addedByUserWorkspace?.user?.fullname ?? null,
            isDeleted: p.addedByUserWorkspace?.user?.isDeleted
          },
          company: {
            name: p.attributedToCompany?.name,
            uniqueCode: p.attributedToCompany?.uniqueCode
          },
          user: {
            fullname: p.obtainedBy?.user?.fullname ?? null,
            isDeleted: p.obtainedBy?.user?.isDeleted
          }
        })) ?? [];

      return {
        data: mappedData,
        pagination: response?.pagination
      };
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/points')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(`${t('toast.error.label')}`);
      }
      throw error;
    }
  }

  function pointsListFilters(): PointFilters {
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const companyUniqueCode =
      (columnFilters.find((filter) => filter.id === 'companyUniqueCode')?.value as string) ?? '';
    const label = (columnFilters.find((filter) => filter.id === 'label')?.value as string) ?? '';
    const beneficiaryFullname =
      (columnFilters.find((filter) => filter.id === 'beneficiaryFullname')?.value as string) ?? '';
    const amountMin =
      (columnFilters.find((filter) => filter.id === 'amountMin')?.value as string) ?? '';
    const amountMax =
      (columnFilters.find((filter) => filter.id === 'amountMax')?.value as string) ?? '';

    const statusFilter = columnFilters.find((filter) => filter.id === 'status');
    const status = statusFilter?.value as string[] | undefined;

    const valueDate =
      (columnFilters.find((filter) => filter.id === 'valueDate')?.value as string) ?? '';
    const valueDateAfter =
      (columnFilters.find((filter) => filter.id === 'valueDateAfter')?.value as string) ?? '';
    const valueDateBefore =
      (columnFilters.find((filter) => filter.id === 'valueDateBefore')?.value as string) ?? '';

    const createdAt =
      (columnFilters.find((filter) => filter.id === 'createdAt')?.value as string) ?? '';
    const createdAtAfter =
      (columnFilters.find((filter) => filter.id === 'createdAtAfter')?.value as string) ?? '';
    const createdAtBefore =
      (columnFilters.find((filter) => filter.id === 'createdAtBefore')?.value as string) ?? '';

    const importedByFullname =
      (columnFilters.find((filter) => filter.id === 'importedByFullname')?.value as string) ?? '';

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    const amountSortValue = sortValue('amount');
    const createdAtSortValue = sortValue('createdAt');
    let valueDateSortValue = sortValue('valueDate');

    if (!amountSortValue && !createdAtSortValue && !valueDateSortValue) {
      valueDateSortValue = 'desc';
    }

    return {
      companyName,
      companyUniqueCode,
      label,
      beneficiaryFullname,
      amountMin,
      amountMax,
      status,
      valueDate,
      valueDateAfter,
      valueDateBefore,
      createdAt,
      createdAtAfter,
      createdAtBefore,
      importedByFullname,
      amountSortValue,
      createdAtSortValue,
      valueDateSortValue
    };
  }

  function getPointDataForExport(point: PointCollection) {
    const sanitize = (value: string | number | undefined | null) =>
      String(value ?? '').replace(/[\r\n\t]/g, '');
    const formatForExportDate = (value?: string | null) =>
      value ? formatDate(new Date(value), 'yyyy-MM-dd') : '';

    return [
      sanitize(point.company?.name),
      sanitize(point.company?.uniqueCode),
      sanitize(point.user?.fullname),
      sanitize(point.amount),
      sanitize(point.label),
      sanitize(point.status),
      sanitize(formatForExportDate(point.valueDate)),
      sanitize(formatForExportDate(point.importedAt)),
      sanitize(point.importedBy?.fullname)
    ];
  }

  const pointsListQuery = useQuery<PointsQueryResult>({
    queryKey: ['points_list'],
    queryFn: async (): Promise<PointsQueryResult> => {
      const points = await pointsListFiltersQuery(pointsListFilters());
      return points;
    }
  });

  const filterFields = usePointsFilterFields();

  const pointsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('points', user.data?.id),
    [user.data?.id]
  );

  const { table } = useDataTable<PointCollection, unknown>({
    data: pointsListQuery.data?.data ?? [],
    columns: usePointsColumns(),
    filterFields,
    pageCount: pointsListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: pointsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    let allData: PointCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil((pointsListQuery.data?.pagination?.totalItems ?? 1) / limit);
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      pointsListFiltersQuery(pointsListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data);

    const fields = [
      t('label.point.company.name'),
      t('label.point.unique.code'),
      t('label.point.beneficiary'),
      t('label.point.amount'),
      t('label.point.pattern'),
      t('label.point.status'),
      t('label.point.import.date'),
      t('label.point.import.date'),
      t('label.point.import.user.fullname')
    ];

    const csv = unparse({
      fields: fields,
      data: allData.map((p) => getPointDataForExport(p))
    });

    const currentDate = new Date();
    const baseFileName = `points-export-${currentDate.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;
    downloadCSV(csv, fileName);

    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      pointsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.points.title')}</span>

      {pointsListQuery.isLoading && (
        <DataTableSkeleton columnCount={9} cellWidths={['24rem', '12rem', '12rem', '8rem']} />
      )}

      {pointsListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />
          <DataTable table={table} totalItems={pointsListQuery.data?.pagination?.totalItems} />
        </>
      )}
    </>
  );
};
